import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import { ThemeProvider } from './theme/ThemeContext';
import { LanguageProvider, useLanguage, isValidLocale } from './i18n/LanguageContext';
import { getMe, logoutOnServer } from './services/api';
import { setActiveTimezone, userTimezone } from './utils/kernelTimezone';

/**
 * Fabbrica dell'App condivisa: sessione (token/utente in localStorage + refresh con getMe),
 * login/logout/aggiornamento profilo, sincronizzazione della lingua, provider di tema e lingua,
 * router, schermata di caricamento e rotta /login. Ogni modulo passa solo ciò che lo differenzia:
 *
 * - `Login`: pagina di login (di solito creata con createLoginPage).
 * - `canAccessPage(user, page)` e `getDefaultPath(user)`: dal proprio menu.js.
 * - `routes(ctx)`: le <Route> del modulo (in un fragment). `ctx` contiene
 *   { t, user, isAuthenticated, defaultPath, Guarded, guard, handleLogin, handleLogout, handleUserUpdate }.
 *   \`guard(page, element)\` = rotta protetta: reindirizza al login se non autenticato e alla pagina
 *   di default se l'utente non può accedere a \`page\`.
 * - `scrollToTop` (default true): scroll a inizio pagina a ogni cambio rotta.
 * - `appClassName` (opzionale): avvolge le rotte in <div className="...">.
 */
export function createApp({ Login, canAccessPage, getDefaultPath, routes, scrollToTop = true, appClassName }) {
  const Guarded = ({ user, page, children }) => {
    if (!user || !canAccessPage(user, page)) {
      return <Navigate to={getDefaultPath(user)} replace />;
    }
    return children;
  };

  function AppContent({ onLangChange }) {
    const { t } = useLanguage();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          const saved = JSON.parse(savedUser);
          setUser(saved);
          setIsAuthenticated(true);
          onLangChange(saved);
        } catch {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
        getMe()
          .then((res) => {
            if (res.user) {
              localStorage.setItem('user', JSON.stringify(res.user));
              setUser(res.user);
              onLangChange(res.user);
            }
          })
          .catch(() => {});
      }
      setLoading(false);
    }, [onLangChange]);

    const handleLogin = (token, userData) => {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setIsAuthenticated(true);
      setUser(userData);
      onLangChange(userData);
    };

    const handleLogout = () => {
      // La sessione si chiude anche sul server (così non conta più nel limite di accessi); se non risponde si esce lo stesso.
      logoutOnServer().catch(() => {});
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setUser(null);
    };

    const handleUserUpdate = (userData) => {
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      onLangChange(userData);
    };

    if (loading) {
      return <div className="loading">{t('common.loading')}</div>;
    }

    const defaultPath = isAuthenticated ? getDefaultPath(user) : '/login';
    // Fuso con cui si mostrano gli orari: quello scelto dall'utente o, se non l'ha scelto, quello dell'azienda.
    const timezone = isAuthenticated && user ? userTimezone(user) : null;
    setActiveTimezone(timezone);
    const guard = (page, element) => (
      isAuthenticated ? <Guarded user={user} page={page}>{element}</Guarded> : <Navigate to="/login" replace />
    );

    const content = (
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to={getDefaultPath(user)} replace /> : <Login onLogin={handleLogin} />
          }
        />
        {Login.ForgotPassword && (
          <Route
            path="/forgot-password"
            element={isAuthenticated ? <Navigate to={getDefaultPath(user)} replace /> : <Login.ForgotPassword />}
          />
        )}
        {routes({ t, user, isAuthenticated, defaultPath, Guarded, guard, handleLogin, handleLogout, handleUserUpdate })}
      </Routes>
    );

    return (
      // key: se cambia il fuso (profilo) le pagine si ricaricano e ricalcolano giorni e orari.
      <Router key={timezone || 'device'}>
        {scrollToTop && <ScrollToTop />}
        {appClassName ? <div className={appClassName}>{content}</div> : content}
      </Router>
    );
  }

  function App() {
    const [lang, setLang] = useState(() => {
      const saved = localStorage.getItem('lang');
      return isValidLocale(saved) ? saved : 'it-IT';
    });

    const storedLang = () => {
      const saved = localStorage.getItem('lang');
      return isValidLocale(saved) ? saved : 'it-IT';
    };

    const syncLang = useCallback((user) => {
      const next = user?.lang && isValidLocale(user.lang) ? user.lang : storedLang();
      setLang(next);
      localStorage.setItem('lang', next);
    }, []);

    const handleSetLang = useCallback((next) => {
      setLang(next);
      localStorage.setItem('lang', next);
    }, []);

    return (
      <ThemeProvider>
        <LanguageProvider lang={lang} setLang={handleSetLang}>
          <AppContent onLangChange={syncLang} />
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  return App;
}
